import * as React from "react";
import { Loader, LoadingOverlay } from "react-overlay-loader";
import { connect } from "react-redux";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../Waveforms.scss";
import { AnalyseState, LoadingStateEnum, ToWaveformNumeric, WaveformType } from "../interfaces/state";
import * as Actions from "../actions";
import ActionDashboard from "./ActionDashboard";
import PlaybackDashboard from "./PlaybackDashboard";
import { Dispatch } from "redux";
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../../../misc/api/WebPortalTokenClient";
import { useCoughMarshalApiClient } from "../../../misc/api/CoughMarshalApiClient";
import { useAuth } from "react-oidc-context";
import { useAboutContextProvider } from "../../../Core/AboutContextProvider";

export interface WaveformContainerProps {
    sessionId: string;
    primaryAnalystSetId: string;
    isScrollActionZoom: boolean;
    zoomLevel: number;
    selectedWaveform: string;
    loadingState: LoadingStateEnum;
    previousPlaybackTimepoint: number;
    selectedSegment: number;
    isSegmented: boolean;

    dispatch?: Dispatch<AnalyseState>;
}

declare const rootUrl: string;

export const WaveformContainer = ({ sessionId, primaryAnalystSetId, isScrollActionZoom, zoomLevel, selectedWaveform, loadingState, dispatch, previousPlaybackTimepoint, selectedSegment, isSegmented }: WaveformContainerProps): JSX.Element => {
    const client = useCoughMarshalApiClient();
    const legacyWebPortalClient = useWebPortalLegacyClient();
    const webPortalClient = useWebPortalApiClient();
    const { user } = useAuth();
    const { about } = useAboutContextProvider();
    const [selectedSegmentMultiDayOffset, setSelectedSegmentMultiDayOffset] = React.useState<number>();

    React.useEffect(() => {

        const multiDayOffset = (selectedSegment * 86400) - 86400;
        setSelectedSegmentMultiDayOffset(multiDayOffset);

        dispatch(
            Actions.InitalizePeaksAsyncAction.create(
                client,
                legacyWebPortalClient,
                webPortalClient.client,
                user,
                sessionId,
                primaryAnalystSetId,
                selectedWaveform,
                previousPlaybackTimepoint,
                selectedSegment,
                false
            )
        );

        return () => { dispatch(Actions.DestoryPeaksAction.create()) };
    }, [sessionId, primaryAnalystSetId, selectedWaveform, dispatch, previousPlaybackTimepoint, selectedSegment]);

    React.useEffect(() => {
        const scrollZoom = (e: WheelEvent) => {
            e.preventDefault();
            if (e.deltaY > 0) {
                dispatch(Actions.SetZoomLevelAction.create(zoomLevel + 1));
            } else {
                dispatch(Actions.SetZoomLevelAction.create(zoomLevel - 1));
            }           
        }

        if (isScrollActionZoom) {
            document.addEventListener("wheel", scrollZoom, { passive: false }); // Passive is required due to this change in Chrome: https://www.chromestatus.com/feature/6662647093133312
        }

        return (): void => document.removeEventListener("wheel", scrollZoom);
    }, [isScrollActionZoom, zoomLevel, dispatch]);

    const waveform = ToWaveformNumeric(selectedWaveform as WaveformType);

    const playbackRoot = `${about.CoughMarshalUri}/api/v1/playback/`;

    return (
        <>
            <ToastContainer
                position="top-right"
                autoClose={5000}
                pauseOnHover
                theme="light"
            />
            <LoadingOverlay>
                <div className="panel panel-default">
                    <div className="panel-body">
                        <PlaybackDashboard />
                        <div id="WaveformWrapper">
                            <div id="WaveformTemplate" className="waveform">
                                <div
                                    className="peak peak-overview-container overview-container"
                                    data-url={isSegmented ? `${playbackRoot}waveform/${sessionId}/0/${waveform}/${selectedSegment}` : `${playbackRoot}waveform/${sessionId}/0/${waveform}`}
                                    data-initial-time={selectedSegmentMultiDayOffset}
                                />
                                <div
                                    className="peak peak-overview-container overview-container"
                                    data-url={isSegmented ? `${playbackRoot}waveform/${sessionId}/3/${waveform}/${selectedSegment}` : `${playbackRoot}waveform/${sessionId}/3/${waveform}`}
                                    data-initial-time={selectedSegmentMultiDayOffset}
                                />
                                <h5>Microphone</h5>
                                <div
                                    className="peak peak-zoom-container left-container"
                                    data-url={isSegmented ? `${playbackRoot}waveform/${sessionId}/1/${waveform}/${selectedSegment}` : `${playbackRoot}waveform/${sessionId}/1/${waveform}`}
                                    data-initial-time={selectedSegmentMultiDayOffset}
                                />
                                <h5>Sensor</h5>
                                <div
                                    className="peak peak-zoom-container right-container"
                                    data-url={isSegmented ? `${playbackRoot}waveform/${sessionId}/2/${waveform}/${selectedSegment}` : `${playbackRoot}waveform/${sessionId}/2/${waveform}`}
                                    data-initial-time={selectedSegmentMultiDayOffset}
                                />
                            </div>
                        </div>
                        <ActionDashboard />
                    </div>
                </div>
                <Loader loading={loadingState !== LoadingStateEnum.Loaded} />
            </LoadingOverlay>
        </>
    );
}

function mapStateToProps(state: AnalyseState): WaveformContainerProps {
    return {
        sessionId: state.core.sessionMetadata.sessionId,
        primaryAnalystSetId: state.analysisSet.primaryAnalystSetId,
        isScrollActionZoom: state.core.isScrollActionZoom,
        zoomLevel: state.core.zoomLevel,
        selectedWaveform: state.core.selectedWaveform,
        isSegmented: state.core.isSegmented,
        loadingState: state.core.loadingState,
        previousPlaybackTimepoint: state.core.previousPlaybackTimepoint,
        selectedSegment: state.core.selectedSegment
    };
}

export default connect<WaveformContainerProps, any, void>(mapStateToProps)(WaveformContainer);
